<template>
  <div v-if="user" id="detailsUser">
    <c-row class="mb-4">
      <c-col>
        <router-link
          v-show="$auth.check('app-admin')"
          :to="{ name: 'myOrganisation', params: { organisationId: user.organisationId } }"
          class="btn btn-primary"
        >
          <c-icon :content="freeSet.cilArrowLeft" />
          <span class="ml-2">{{ organisation.name }}</span>
        </router-link>
        <router-link
          v-show="$auth.check('organisation-admin')"
          :to="{ name: 'myOrganisation' }"
          class="btn btn-primary"
        >
          <c-icon :content="freeSet.cilArrowLeft" />
          <span class="ml-2">{{ organisation.name }}</span>
        </router-link>
      </c-col>
      <c-col>
        <c-button-group class="float-right">
          <router-link :to="{ name: 'editUser', params: { userId: user._id } }" class="btn btn-primary">
            Edit
          </router-link>
          <c-button v-if="canDeleteUser" color="danger" @click="openModalConfirm">
            Delete
          </c-button>
        </c-button-group>
      </c-col>
    </c-row>

    <c-card>
      <c-card-body>
        <h1>{{ [user.firstName, user.middleName, user.lastName].join(' ') }}</h1>
        <p class="text-h6 is-4">{{ organisation.name }}</p>
        <document-info :doc="user"></document-info>
      </c-card-body>
    </c-card>

    <c-card>
      <c-card-body>
        <h2>Account information</h2>
        <table class="table">
          <tbody>
            <tr>
              <td>Email</td>
              <td>
                {{ user.username }}
                <c-button size="sm" color="primary" @click="showChangeEmail = true">Change</c-button>
                <username-change-request :show="showChangeEmail" :user="user" @close="showChangeEmail = false" />
              </td>
            </tr>
            <tr>
              <td>Password</td>
              <td>
                <c-button-group>
                  <c-button size="sm" color="primary" @click="showChangePassword = true">Change password</c-button>
                  <c-button size="sm" color="primary" @click="showResetPassword = true">Send password reset</c-button>
                </c-button-group>
                <change-password :show="showChangePassword" :user="user" @close="showChangePassword = false" />
                <send-reset-password :show="showResetPassword" :user="user" @close="showResetPassword = false" />
              </td>
            </tr>
            <tr>
              <td>Role</td>
              <td>{{ roleData.name }}</td>
            </tr>
            <tr v-if="user.groupIds && user.groupIds.length">
              <td>
                Group
                <span v-if="user.groupIds.length > 1">s</span>
              </td>
              <td>
                <c-badge
                  v-for="(groupName, i) in getGroupNames(user.groupIds)"
                  :key="`${i}_group`"
                  color="secondary"
                  class="p-2 mr-2"
                >
                  {{ groupName }}
                </c-badge>
              </td>
            </tr>
            <tr>
              <td>Account confirmed</td>
              <td>
                {{ user.accountConfirmed ? 'Yes - email address confirmed' : 'No - email address not confirmed' }}
              </td>
            </tr>
            <tr>
              <td>Allow login</td>
              <td>{{ user.allowLogin ? 'Yes - user can login' : 'No - user cannot login' }}</td>
            </tr>
            <tr>
              <td>MFA enabled</td>
              <td>{{ user.security.mfa.active ? 'Yes' : 'No' }}</td>
            </tr>
          </tbody>
        </table>
      </c-card-body>
    </c-card>

    <c-modal title="Delete this user?" :show="confirmDeleteUser">
      <p class="lead">Delete this user and all associated information? Cannot undo.</p>
      <template v-slot:footer>
        <c-button-group>
          <c-button color="danger" @click="deleteUser">
            Delete user
          </c-button>
          <c-button color="secondary" @click="confirmDeleteUser = false">Cancel</c-button>
        </c-button-group>
      </template>
    </c-modal>

    <user-logs v-if="user" :user="user"></user-logs>
  </div>
</template>

<script>
import _ from 'lodash';
import { freeSet } from '@coreui/icons';
import DocumentInfo from '../components/DocumentInfo';
import UserLogs from '../components/user-logs.vue';
import { buildEndpoint } from '@/libs/buildEndPoint';
import { getRoleData } from '@/libs/user/getRoleData';
import UsernameChangeRequest from './components/username-change-request.vue';
import ChangePassword from './components/change-password.vue';
import SendResetPassword from './components/reset-password';

export default {
  name: 'UserDetails',
  components: {
    DocumentInfo,
    UserLogs,
    UsernameChangeRequest,
    ChangePassword,
    SendResetPassword,
  },
  data() {
    return {
      freeSet,
      user: null,
      organisation: null,
      confirmDeleteUser: false,
      confirmModal: null,
      showChangeEmail: false,
      showChangePassword: false,
      showResetPassword: false,
      roleData: null,
      canDeleteUser: false,
    };
  },
  mounted() {
    this.get();
  },
  methods: {
    deleteUser() {
      this.confirmDeleteUser = false;
      this.$http
        .delete(buildEndpoint('organisation-admin/organisation/users/delete/') + this.$route.params.userId)
        .then((response) => {
          if (response.data.success) {
            this.$store.commit('addSuccessMessage', {
              header: 'Success',
              message: 'User deleted',
            });
            this.$router.push({ name: 'myOrganisation' });
          } else {
            this.$store.commit('addErrorMessage', {
              header: 'Error',
              message: response.data.message,
            });
          }
        })
        .catch((error) => {
          this.$store.commit('addErrorMessage', {
            header: 'Error',
            message: error.message,
          });
        });
    },
    get() {
      this.$http
        .get(buildEndpoint('organisation-admin/organisation/users/get/') + this.$route.params.userId)
        .then((response) => {
          if (response.data.success) {
            this.user = response.data.data.user;
            this.organisation = response.data.data.organisation;
            this.roleData = getRoleData(this.user.role, this.$store);
            this.canDeleteUser = this.$route.params.userId !== this.$store.state.app.authenticatedUser.id;
          } else {
            this.$store.commit('addErrorMessage', {
              header: 'Error',
              message: response.data.message,
            });
          }
        })
        .catch((error) => {
          this.$store.commit('addErrorMessage', {
            header: 'Error',
            message: error.message,
          });
        });
    },
    openModalConfirm() {
      this.confirmDeleteUser = true;
    },
    getGroupNames() {
      const groupNames = [];
      if (this.user.groupIds && this.organisation.groups) {
        this.user.groupIds.forEach((groupId) => {
          const groupData = _.find(this.organisation.groups, { _id: groupId });
          if (groupData && groupData.name) {
            groupNames.push(groupData.name);
          }
        });
      }
      return groupNames;
    },
  },
};
</script>

<style lang="scss" scoped="">
table {
  tr {
    td {
      &:first-child {
        font-weight: bold;
        width: 200px;
      }
    }
  }
}
</style>
